import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormGrid } from '@/components/ui/FormGrid';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinDeferredRevenue } from '@/types/finance';
import { notify } from '@/core/toast/notify';

export function FinanceRevenuePage() {
  const [items, setItems] = useState<FinDeferredRevenue[]>([]);
  const [form, setForm] = useState({
    sourceType: 'SUBSCRIPTION',
    totalAmount: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 12)).toISOString().slice(0, 10),
    description: '',
  });

  const { rows: paginatedItems, tablePaginationProps: itemsPaginationProps } = useClientPaginatedRows(items);

  const load = useCallback(() => {
    financeApi.listDeferredRevenue().then(setItems).catch((e) => showApiError(e, 'Failed to load deferred revenue'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    const totalAmount = Number(form.totalAmount);
    if (!totalAmount || totalAmount <= 0) {
      notify.error('Enter a valid amount');
      return;
    }
    try {
      await financeApi.createDeferredRevenue({ ...form, totalAmount });
      notify.success('Deferred revenue schedule created');
      load();
    } catch (e) {
      showApiError(e, 'Create failed');
    }
  };

  const recognize = async () => {
    try {
      const r = await financeApi.recognizeDueRevenue();
      notify.success(`Recognized ${r.recognized} schedule lines`);
      load();
    } catch (e) {
      showApiError(e, 'Recognition run failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Revenue recognition"
        description="Deferred revenue schedules and ASC 606 straight-line recognition"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Revenue' }]}
        actions={<Button variant="secondary" onClick={() => void recognize()}>Run recognition</Button>}
      />
      <FormGrid columns={4} className="card p-4 mb-4">
        <Input value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} placeholder="Source type" />
        <Input type="number" value={form.totalAmount} onChange={(e) => setForm({ ...form, totalAmount: e.target.value })} placeholder="Total amount" />
        <DateRangeFields
          from={form.startDate}
          to={form.endDate}
          onFromChange={(v) => setForm({ ...form, startDate: v })}
          onToChange={(v) => setForm({ ...form, endDate: v })}
        />
        <Button onClick={() => void create()}>Create schedule</Button>
      </FormGrid>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Source</th>
              <th className="p-2 text-right">Total</th>
              <th className="p-2 text-right">Recognized</th>
              <th className="p-2 text-left">Period</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((item) => (
              <tr key={item.id} className="border-b">
                <td className="p-2">{item.sourceType}{item.sourceId != null ? ` #${item.sourceId}` : ''}</td>
                <td className="p-2 text-right font-mono">{item.totalAmount?.toFixed(2)}</td>
                <td className="p-2 text-right font-mono">{item.recognizedAmount?.toFixed(2)}</td>
                <td className="p-2">{item.startDate} → {item.endDate}</td>
                <td className="p-2">{item.statusCode}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination {...itemsPaginationProps} hasRows={items.length > 0} />
      </div>
    </div>
  );
}
